/**
 * The signed-in user, as an avatar in the far right of the header.
 *
 * The name and email used to sit inline in the header, which cost two lines of
 * text next to controls that need the room. They move into a popup behind an
 * initials disc: identity at the top, then the account actions.
 *
 * Theme has a quick switch here rather than occupying permanent header space;
 * Preferences keeps the full theme choice alongside startup, chart appearance,
 * access and reset. Closes on outside click and on Escape, like the chart menu.
 *
 * **Administration** appears only for an account that holds one of the admin
 * permissions, and the disc itself carries the standing: a red ring for an
 * administrator, a ring and a dot for root. Both are here because the app used
 * to give no answer at all to "am I an administrator on this deployment?" -
 * the page was reached by typing a URL nothing linked to, and a missing
 * section looked the same as a feature that had not shipped. The word is
 * repeated under the email, because a coloured ring is a hint, not a label.
 *
 * With Microsoft sign-in switched off locally, the menu becomes the persona
 * switcher: the same popup lists the development identities the backend offers
 * and picking one reloads the page as that person. It is how permissions and
 * roles get exercised without five Entra accounts, and it cannot appear in a
 * deployed environment - the list is empty unless the backend reports
 * authentication off.
 */

import { toursPanelOpen } from '../features/tours/tour-engine.ts';
import { availableTours } from '../features/tours/tour-registry.ts';
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../lit-base.ts';
import { StoreController } from '../state/controller.ts';
import { authState, signOut } from './auth.ts';
import { currentPersonaKey, devPersonas, isDevIdentity, setPersona } from './dev-identity.ts';
import { userInitials } from './profile.ts';
import { openPreferences } from '../features/preferences/preferences-panel.ts';
import { openReleaseNotes, releaseNotesCount } from '../state/announcements.ts';
import { administrationHref, adminRole } from '../state/admin-access.ts';
import { backendGitSha, backendVersion, isRootUser, myPermissions } from '../state/store.ts';
import { getTheme, toggleTheme } from '../ui/theme.ts';
import { rerenderChartFromLast } from '../features/plot/plot-actions.ts';
import { redrawDashboardTiles } from '../features/dashboard/dashboard-tile.ts';

/** The word shown for each standing. Root is not "admin": it can do more. */
const ROLE_LABEL: Record<'root' | 'admin', string> = { root: 'Root', admin: 'Administrator' };

@customElement('user-menu')
export class UserMenu extends LightElement {
  // Permissions land after the first paint, so the Administration entry and the
  // ring on the disc have to re-render when they do - otherwise an
  // administrator sees a menu that says they are not one.
  private store = new StoreController(this, [authState, myPermissions, isRootUser, releaseNotesCount, backendVersion, backendGitSha]);

  @state() private open = false;

  private onDocClick = (e: MouseEvent): void => {
    if (!this.open) return;
    const root = this.querySelector('.user-menu');
    if (root && !root.contains(e.target as Node)) this.open = false;
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.open = false;
  };

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKey);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKey);
  }

  override render(): TemplateResult | typeof nothing {
    const user = authState.value.user;
    if (!user) return nothing;
    const role = adminRole();
    const dark = getTheme() === 'dark';
    return html`
      <div class="user-menu">
        <button
          class="user-avatar ${this.open ? 'active' : ''} ${role ? `is-${role}` : ''}"
          title=${role ? `${user.name} - ${user.email} (${ROLE_LABEL[role]})` : `${user.name} - ${user.email}`}
          aria-haspopup="menu"
          aria-expanded=${this.open ? 'true' : 'false'}
          aria-label="Account"
          @click=${(e: Event) => {
            e.stopPropagation();
            this.open = !this.open;
          }}
        >
          ${userInitials(user)}
        </button>
        ${this.open
          ? html`<div class="user-popup" role="menu">
              <div class="user-popup-head">
                <span class="user-popup-avatar ${role ? `is-${role}` : ''}" aria-hidden="true">
                  ${userInitials(user)}
                </span>
                <span class="user-popup-id">
                  <span class="user-popup-name">${user.name}</span>
                  <span class="user-popup-email" title=${user.email}>${user.email}</span>
                  ${role
                    ? html`<span class="user-popup-role is-${role}">${ROLE_LABEL[role]}</span>`
                    : nothing}
                </span>
              </div>
              ${this.renderPersonas()}
              <div class="user-popup-actions">
                <button
                  class="user-popup-item user-popup-theme"
                  role="menuitemcheckbox"
                  aria-checked=${dark ? 'true' : 'false'}
                  title=${dark ? 'Switch to light mode' : 'Switch to dark mode'}
                  @click=${() => {
                    toggleTheme();
                    // uPlot and dashboard tiles cache colours at render time.
                    rerenderChartFromLast();
                    redrawDashboardTiles();
                    this.requestUpdate();
                  }}
                >
                  <span class="user-popup-icon" aria-hidden="true">${dark ? '☾' : '☀'}</span>
                  Dark mode
                  <span class="user-theme-switch ${dark ? 'is-on' : ''}" aria-hidden="true"></span>
                </button>
                <button
                  class="user-popup-item"
                  role="menuitem"
                  title="Startup, theme, chart appearance and what this account may do"
                  @click=${() => {
                    this.open = false;
                    openPreferences();
                  }}
                >
                  <span class="user-popup-icon" aria-hidden="true">⚙</span>
                  Preferences
                </button>
                ${availableTours().length
                  ? html`<button
                      class="user-popup-item"
                      role="menuitem"
                      data-opens-tours
                      title="Step-by-step tours on the real page"
                      @click=${() => {
                        this.open = false;
                        toursPanelOpen.set(true);
                      }}
                    >
                      <span class="user-popup-icon" aria-hidden="true">▶</span>
                      Guided tours
                    </button>`
                  : nothing}
                ${releaseNotesCount.value > 0
                  ? html`<button
                      class="user-popup-item"
                      role="menuitem"
                      title="Release notes from the administrators"
                      @click=${() => {
                        this.open = false;
                        void openReleaseNotes();
                      }}
                    >
                      <span class="user-popup-icon" aria-hidden="true">✦</span>
                      What's new
                    </button>`
                  : nothing}
                ${role
                  ? html`<a
                      class="user-popup-item"
                      role="menuitem"
                      href=${administrationHref()}
                      title="People, groups, features, pages and the activity log"
                      @click=${() => {
                        this.open = false;
                      }}
                    >
                      <span class="user-popup-icon" aria-hidden="true">❖</span>
                      Administration
                    </a>`
                  : nothing}
                ${isDevIdentity()
                  ? nothing
                  : html`<button
                      class="user-popup-item"
                      role="menuitem"
                      title="Sign out of Microsoft"
                      @click=${() => {
                        this.open = false;
                        void signOut();
                      }}
                    >
                      <span class="user-popup-icon" aria-hidden="true">→</span>
                      Sign out
                    </button>`}
                <!-- The version lives here, not beside the name. Several fast
                     clicks still sail the header ship (app-root, version-click). -->
                <div
                  class="user-popup-version"
                  title=${this.versionTooltip()}
                  @click=${() => this.dispatchEvent(new CustomEvent('version-click', { bubbles: true }))}
                >
                  v${__APP_VERSION__}
                </div>
              </div>
            </div>`
          : nothing}
      </div>
    `;
  }

  /** The frontend (bundle) version always, plus the backend version and git SHA
   * once the backend has reported them. */
  private versionTooltip(): string {
    const lines = [`Frontend v${__APP_VERSION__}`];
    const bv = backendVersion.value;
    if (bv) lines.push(`Backend v${bv}`);
    const sha = backendGitSha.value;
    if (sha) lines.push(`Commit ${sha.slice(0, 12)}`);
    return lines.join('\n');
  }

  /** The development persona list, or nothing at all when sign-in is real. */
  private renderPersonas(): TemplateResult | typeof nothing {
    if (!isDevIdentity()) return nothing;
    const personas = devPersonas();
    if (!personas.length) return nothing;
    const active = currentPersonaKey();
    return html`
      <div class="user-popup-personas">
        <div class="user-popup-personas-head">
          Development identity
          <span class="user-popup-soon">no sign-in</span>
        </div>
        ${personas.map(
          (persona) => html`
            <button
              class="user-popup-item ${persona.key === active ? 'active' : ''}"
              role="menuitemradio"
              aria-checked=${persona.key === active ? 'true' : 'false'}
              title=${persona.summary}
              @click=${() => {
                if (persona.key !== active) setPersona(persona.key);
                this.open = false;
              }}
            >
              <span class="user-popup-icon" aria-hidden="true">
                ${persona.key === active ? '●' : '○'}
              </span>
              ${persona.name}
              <span class="user-popup-soon">${persona.role}</span>
            </button>
          `,
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'user-menu': UserMenu;
  }
}
